import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
// import { Plus } from "@phosphor-icons/react";
import MerchantLayout from "@/components/MerchantLayout";
import TablePagination from "@/components/TablePagination";
import { StatusPill } from "@/components/Layout";
import { MERCHANT } from "@/lib/routes";
import { mApi } from "@/lib/api";
import { merchantCardsApi } from "@/lib/merchantCards";
import {
  cardholderBlonixId,
  cardholderDisplayName,
  formatCardholderContact,
} from "@/lib/cardholderContact";
// import { ApplyCardModal, BindCardModal } from "@/pages/m/MerchantCardModals";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PAGE_SIZE = 25;

export function MerchantCards() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await merchantCardsApi.list({ page, limit });
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [limit]);

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-cards-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Cards</h1>
                <p className="text-sm text-zinc-500 tabular-nums">{total} cards</p>
              </div>
            </div>
            <Surface>
              <Table cols={["Card", "Type", "Status", "Expiry", "Balance", "Created", ""]}>
                {loading && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-zinc-500">Loading…</td></tr>
                )}
                {!loading && items.map((c) => (
                  <tr key={c.id} className="border-b border-zinc-50 hover:bg-zinc-50/60">
                    <td className="px-5 py-3 font-mono text-xs">{c.masked_pan || c.id?.slice(0, 12) || "—"}</td>
                    <td className="px-5 py-3 capitalize">{c.type}</td>
                    <td className="px-5 py-3"><StatusPill value={c.status} /></td>
                    <td className="px-5 py-3 font-mono text-xs">{c.expiry || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(c.balance)}</td>
                    <td className="px-5 py-3 text-xs text-zinc-500">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="px-5 py-3 text-right">
                      <Link to={MERCHANT.card(c.id)} className="text-xs font-medium text-zinc-700 hover:text-zinc-950 underline-offset-2 hover:underline">Manage</Link>
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-zinc-500">No cards yet.</td></tr>
                )}
              </Table>
              <TablePagination
                page={page}
                pages={pages}
                total={total}
                limit={limit}
                loading={loading}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </Surface>
          </div>
    </MerchantLayout>
  );
}

const CARDHOLDER_PAGE_SIZE = 25;

export function MerchantUsers() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(CARDHOLDER_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await mApi.get("/merchant/users", { params: { page, limit } });
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [limit]);

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-users-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Cardholders</h1>
        <p className="text-sm text-zinc-500 mb-6 tabular-nums">{total} cardholders</p>
        <Surface>
          <Table cols={["Cardholder ID", "Name", "Email", "Phone", "KYC", "Created"]}>
            {loading && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">Loading…</td></tr>
            )}
            {!loading && items.map((u) => (
              <tr key={u.id} className="border-b border-zinc-50">
                <td className="px-5 py-3 font-mono text-xs">{cardholderBlonixId(u)}</td>
                <td className="px-5 py-3 font-medium">{cardholderDisplayName(u)}</td>
                <td className="px-5 py-3 text-xs">{formatCardholderContact(u)}</td>
                <td className="px-5 py-3 text-xs">{u.phone || "—"}</td>
                <td className="px-5 py-3">
                  <StatusPill value={u.kyc_status} />
                </td>
                <td className="px-5 py-3 text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">No cardholders yet.</td></tr>
            )}
          </Table>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </Surface>
      </div>
    </MerchantLayout>
  );
}

export function MerchantTransactions() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await merchantCardsApi.cardTransactions({ page, limit });
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [limit]);

  const txWhen = (t) => {
    const raw = t.createTime || t.timestamp || t.createdAt;
    if (!raw) return "—";
    const s = String(raw);
    return s.length > 19 ? s.slice(0, 19).replace("T", " ") : s.replace("T", " ");
  };

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-tx-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Card transactions</h1>
        <p className="text-sm text-zinc-500 mb-6">
          Issuer spend history across all cardholder cards ({total} in range).
        </p>
        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
        <Surface>
          <Table cols={["When", "Cardholder", "Card", "Merchant", "Amount", "Status"]}>
            {loading && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">Loading…</td></tr>
            )}
            {!loading && items.map((t) => (
              <tr key={t.orderId || `${t.card_id}-${t.createTime}`} className="border-b border-zinc-50">
                <td className="px-5 py-3 text-xs font-mono text-zinc-500">{txWhen(t)}</td>
                <td className="px-5 py-3 text-sm">{t.cardholder_name || "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{t.masked_pan || (t.card_id || "").slice(0, 12)}</td>
                <td className="px-5 py-3 text-xs">{t.merchantName || t.bizDesc || t.description || "—"}</td>
                <td className="px-5 py-3 text-right font-mono tabular-nums">{fmt$(t.final_price ?? t.platformPrincipal)}</td>
                <td className="px-5 py-3"><StatusPill value={t.status_label || t.status} /></td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">No card transactions in the last 90 days.</td></tr>
            )}
          </Table>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </Surface>
      </div>
    </MerchantLayout>
  );
}

export function MerchantAudit() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await mApi.get("/merchant/audit-logs", { params: { page, limit } });
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [limit]);

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-audit-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">Audit log</h1>
        <p className="text-sm text-zinc-500 mb-6 tabular-nums">{total} entries</p>
        <Surface>
          <Table cols={["When", "Action", "Method", "Path", "IP", "Status"]}>
            {loading && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">Loading…</td></tr>
            )}
            {!loading && items.map((a) => (
              <tr key={a.id} className="border-b border-zinc-50">
                <td className="px-5 py-3 text-xs font-mono text-zinc-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-5 py-3 font-mono text-xs">{a.action}</td>
                <td className="px-5 py-3"><span className="text-[10px] font-mono uppercase tracking-[0.05em] px-1.5 py-0.5 rounded-sm bg-zinc-100">{a.method}</span></td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-600">{a.path}</td>
                <td className="px-5 py-3 font-mono text-xs">{a.ip_address || "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{a.status_code}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">No audit entries yet.</td></tr>}
          </Table>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            loading={loading}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </Surface>
      </div>
    </MerchantLayout>
  );
}

export function MerchantUsage() {
  const [items, setItems] = useState([]);
  const [topPaths, setTopPaths] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await mApi.get("/merchant/usage", { params: { page, limit } });
      setItems(r.data.items || []);
      setTopPaths(r.data.top_paths || []);
      setTotal(r.data.total ?? 0);
      setPages(r.data.pages ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [limit]);

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-usage-page">
        <h1 className="font-display text-3xl tracking-tight font-medium mb-1">API Usage</h1>
        <p className="text-sm text-zinc-500 mb-6 tabular-nums">{total} API calls</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Surface className="lg:col-span-2 ">
            <Table cols={["When", "Method", "Path", "Status", "Latency", "IP"]}>
              {loading && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">Loading…</td></tr>
              )}
              {!loading && items.map((u, i) => (
                <tr key={`${u.ts}-${i}`} className="border-b border-zinc-50">
                  <td className="px-5 py-3 text-xs font-mono text-zinc-500">{(u.ts || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-5 py-3 text-xs font-mono">{u.method}</td>
                  <td className="px-5 py-3 text-xs font-mono text-zinc-600">{u.path}</td>
                  <td className="px-5 py-3 text-xs font-mono">{u.status_code}</td>
                  <td className="px-5 py-3 text-right text-xs font-mono tabular-nums">{u.latency_ms} ms</td>
                  <td className="px-5 py-3 text-xs font-mono">{u.ip || "—"}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-zinc-500">No API calls yet.</td></tr>}
            </Table>
            <TablePagination
              page={page}
              pages={pages}
              total={total}
              limit={limit}
              loading={loading}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </Surface>
          <Surface className="p-5">
              <div className="font-display text-lg tracking-tight font-medium mb-4">Top endpoints</div>
              <ul className="space-y-2">
                {topPaths.map((p) => (
                  <li key={p.path} className="flex justify-between text-sm">
                    <span className="font-mono text-xs text-zinc-700 truncate flex-1 mr-3">{p.path}</span>
                    <span className="font-mono tabular-nums">{p.count}</span>
                  </li>
                ))}
                {topPaths.length === 0 && <li className="text-xs text-zinc-500">No data.</li>}
              </ul>
          </Surface>
        </div>
      </div>
    </MerchantLayout>
  );
}

function Surface({ children, className = "" }) {
  return <div className={`bg-white border border-zinc-200 rounded-sm ${className}`}>{children}</div>;
}
function Table({ cols, children }) {
  return (
    <div className="table-scroll">
      <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500 border-b border-zinc-100">
          {cols.map((c) => <th key={c} className={`px-5 py-3 ${c === "Amount" || c === "Balance" || c === "Latency" ? "text-right" : ""}`}>{c}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    </div>
  );
}
